import { createStore, useAtomValue, useSetAtom } from 'jotai'
import { forwardRef, useCallback, useImperativeHandle } from 'react'
import { ThankYouScreen } from '../views'
import {
	thankYouScreenChangeCallbackAtom,
	thankYouScreenIsGeneratingAtom,
	thankYouScreenLifecycleAtom,
	thankYouScreenResetAllCallbackAtom,
	thankYouScreenResetCallbackAtom,
	thankYouScreensAtom,
	thankYouScreenSelectedIndexAtom,
	useThankYouScreenStore,
} from './state'
import { ThankYouPageSimulator } from './thank-you-page-simulator'
import { ThankYouPageTabs } from './thank-you-page-tabs'

export type ThankYouPageEditorRef = {
	resetToDefault: () => Promise<void>
	resetAllToDefault: () => Promise<void>
}

export type ThankYouPageEditorProps = {
	store?: ReturnType<typeof createStore>
}

export const ThankYouPageEditor = forwardRef<ThankYouPageEditorRef, ThankYouPageEditorProps>(
	function ThankYouPageEditor({ store: overrideStore }, ref) {
		const store = useThankYouScreenStore({ overrideStore })

		const onScreenChange = useAtomValue(thankYouScreenChangeCallbackAtom, { store })
		const onReset = useAtomValue(thankYouScreenResetCallbackAtom, { store })
		const onResetAll = useAtomValue(thankYouScreenResetAllCallbackAtom, { store })

		const screensOrUndefined = useAtomValue(thankYouScreensAtom, { store })
		const screens = screensOrUndefined ?? []
		const setScreens = useSetAtom(thankYouScreensAtom, { store })
		const lifecycle = useAtomValue(thankYouScreenLifecycleAtom, { store })
		const isGenerating = useAtomValue(thankYouScreenIsGeneratingAtom, { store })
		const selectedIndex = useAtomValue(thankYouScreenSelectedIndexAtom, { store })
		const setSelectedIndex = useSetAtom(thankYouScreenSelectedIndexAtom, { store })

		const selectedScreen = screens[selectedIndex]

		const updateScreen = useCallback((screenId: string, updates: Partial<ThankYouScreen>) => {
			setScreens((prev) =>
				(prev ?? []).map((screen) => screen.id === screenId ? { ...screen, ...updates } : screen)
			)
		}, [setScreens])

		const handleTitleChange = useCallback(
			(title: string) => {
				if (selectedScreen) {
					onScreenChange?.({
						screen: selectedScreen,
						field: 'title',
						oldValue: selectedScreen.title,
						newValue: title,
					})
					updateScreen(selectedScreen.id, { title })
				}
			},
			[selectedScreen, updateScreen, onScreenChange],
		)

		const handleDescriptionChange = useCallback(
			(description: string) => {
				if (selectedScreen) {
					onScreenChange?.({
						screen: selectedScreen,
						field: 'description',
						oldValue: selectedScreen.description,
						newValue: description,
					})
					updateScreen(selectedScreen.id, { description })
				}
			},
			[selectedScreen, updateScreen, onScreenChange],
		)

		const resetToDefault = useCallback(async () => {
			if (!onReset || !selectedScreen) { return }
			onReset(selectedScreen)
		}, [onReset, selectedScreen])

		const resetAllToDefault = useCallback(async () => {
			if (!onResetAll) { return }
			onResetAll()
		}, [onResetAll])

		useImperativeHandle(ref, () => ({
			resetToDefault,
			resetAllToDefault,
		}), [resetToDefault, resetAllToDefault])

		const isPending = lifecycle === 'pending'
		const isLoading = isPending || screens.length === 0

		return (
			<div>
				<ThankYouPageTabs
					screens={screens}
					selectedIndex={selectedIndex}
					onSelectIndex={setSelectedIndex}
					isLoading={isLoading}
				/>
				<ThankYouPageSimulator
					screen={selectedScreen}
					isGenerating={isGenerating}
					isLoading={isLoading}
					onTitleChange={handleTitleChange}
					onDescriptionChange={handleDescriptionChange}
				/>
			</div>
		)
	},
)
